import { motion, type Variant } from 'framer-motion';
import { Book, FileText, Globe, Search, Hash, Filter, Database, type LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { KnowledgeBase } from '@/api/knowledge-base/knowledge-base.types';
import { FadeId } from '@/components/ui/fade-id';

const itemVariants = {
    hidden: {
        opacity: 0,
        y: 10
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            duration: 0.3
        }
    }
};

interface KnowledgeBaseCardProps {
    knowledgeBase: KnowledgeBase;
    onDelete?: () => void;
    onEdit?: () => void;
    variants?: {
        hidden?: Variant;
        visible?: Variant;
    };
}

const searchTypeLabels = {
    'full_text': 'Full Text',
    'semantic': 'Semantic',
    'hybrid': 'Hybrid',
    'bm25': 'BM25'
};

// StatButton component for grid items
const StatButton = ({
    icon: Icon,
    value,
    label,
    tooltip,
    active,
}: {
    icon: LucideIcon;
    value: string | number;
    label: string;
    tooltip: string;
    active?: boolean;
}) => (
    <TooltipProvider delayDuration={200}>
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    className={cn(
                        "h-11 flex flex-col items-center justify-center gap-0 px-2.5 py-2 transition-colors",
                        active ? "bg-primary/5" : "hover:bg-muted/50"
                    )}
                >
                    <div className="flex items-center gap-1.5 mb-0.5">
                        <Icon className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-semibold">{value}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground uppercase tracking-wide leading-tight">
                        {label}
                    </span>
                </div>
            </TooltipTrigger>
            <TooltipContent>
                <p className="text-xs">{tooltip}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export function KnowledgeBaseCard({ knowledgeBase, variants }: KnowledgeBaseCardProps) {
    const navigate = useNavigate();
    const fileCount = knowledgeBase.metadata?.sources?.fileIds?.length || 0;
    const websiteCount = knowledgeBase.metadata?.sources?.websites?.length || 0;
    
    const searchType = knowledgeBase.searchType || 'semantic';
    const searchTypeLabel = searchTypeLabels[searchType];
    
    const totalSources = fileCount + websiteCount;
    
    // Get chunking configuration
    const targetSplits = knowledgeBase.chunkingConfig?.targetSplitsPerChunk || 20;
    const removeStopWords = knowledgeBase.chunkingConfig?.removeStopWords ?? false;

    return (
        <motion.div variants={variants || itemVariants}>
            <Card
                className="group relative overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
                onClick={() => navigate(`/dashboard/knowledge-base/${knowledgeBase.id}`)}
            >
                {/* Subtle gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Main Content */}
                <div className="relative p-5 space-y-4">
                    {/* Header with Icon, Title, Date and ID */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                                <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-primary/10 ring-1 ring-primary/10 shadow-sm">
                                    <Book className="h-5 w-5 text-primary" />
                                </div>
                            </div>

                            {/* Title and Date */}
                            <div className="min-w-0 flex-1">
                                <h3 
                                    className="text-base font-semibold tracking-tight truncate mb-1"
                                    title={knowledgeBase.name || 'Unnamed Knowledge Base'}
                                >
                                    {knowledgeBase.name || 'Unnamed Knowledge Base'}
                                </h3>
                            </div>
                        </div>

                        {/* Knowledge Base ID on Right */}
                        <div onClick={(e) => e.stopPropagation()}>
                            <FadeId 
                                id={knowledgeBase.id} 
                                label="Knowledge Base ID"
                                tooltipSide="top"
                            />
                        </div>
                    </div>

                    {/* Unified Configuration Grid with Dividers */}
                    <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 1 - Search Type, Files, Websites */}
                            <StatButton
                                icon={Search}
                                value={searchTypeLabel}
                                label="Search Type"
                                tooltip={`Search method: ${searchTypeLabel}`}
                            />
                            
                            <StatButton
                                icon={FileText}
                                value={fileCount}
                                label="Files"
                                tooltip={`${fileCount} file${fileCount !== 1 ? 's' : ''} in knowledge base`}
                                active={fileCount > 0}
                            />

                            <StatButton
                                icon={Globe}
                                value={websiteCount}
                                label="Websites"
                                tooltip={`${websiteCount} website${websiteCount !== 1 ? 's' : ''} in knowledge base`}
                                active={websiteCount > 0}
                            />
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div className="border-t border-border" />
                        
                        <div className="grid grid-cols-3 divide-x divide-border">
                            {/* Row 2 - Total Sources, Chunk Size, Stop Words */}
                            <StatButton
                                icon={Database}
                                value={totalSources}
                                label="Total Sources"
                                tooltip={`${totalSources} total data source${totalSources !== 1 ? 's' : ''}`}
                                active={totalSources > 0}
                            />

                            <StatButton
                                icon={Hash}
                                value={targetSplits}
                                label="Chunk Size"
                                tooltip={`Target splits per chunk: ${targetSplits}`}
                            />

                            <StatButton
                                icon={Filter}
                                value={removeStopWords ? 'Yes' : 'No'}
                                label="Stop Words"
                                tooltip={`Remove stop words: ${removeStopWords ? 'Enabled' : 'Disabled'}`}
                                active={removeStopWords}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </motion.div>
    );
} 